import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { getPageName, getUiInterface, getSuccessMessage, getErrorMessage, getWordConnector } from "../../../locales/translationUtils";
import { useWarehouse } from "../../../apis/warehouse/WarehouseProvider";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import Warehouse from "../../../types/warehouse";
import ColorPicker from "../../../components/ColorPicker";
import AlertOverlay from "../../../components/Alert";

const ViewWarehouseById: React.FC = () => {
    const { t } = useTranslation();
    const [alert, setAlert] = useState<{
        message: string;
        variant: "success" | "danger" | "warning" | "info";
    } | null>(null);
    const { id } = useParams<{ id: string }>();
    const { fetchWarehouseById, excluiWarehouse, putWarehouse } = useWarehouse();
    const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

    const navItems: NavBarItem[] = [
        { label: t('warehouse.labels.home'), route: "/", iconName: "house-door" },
        { label: t('warehouse.labels.list'), route: "/Warehouse", iconName: "list" },
        { label: t('warehouse.labels.new'), route: "/Warehouse/create", iconName: "plus-circle" },
    ];

    useEffect(() => {
        if (!id) throw new Error("ID inválido");

        const loadWarehouse = async () => {
            try {
                setLoading(true);
                setError(false);

                if (!id) throw new Error("ID inválido");

                const data = await fetchWarehouseById(Number(id));

                setWarehouse(data);
            } catch (err) {
                console.error("Erro ao carregar os dados:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        loadWarehouse();
    }, [id, fetchWarehouseById]);

    const handleEdit = async () => {
        if (!warehouse) return;

        if (isEditing && warehouse.id) {
            try {
                const nullRequiredFields: string[] = [];

                if (!warehouse.name) nullRequiredFields.push("Name");
                if (!warehouse.location?.latitude) nullRequiredFields.push("Lat");
                if (!warehouse.location?.longitude) nullRequiredFields.push("Lng");
                if (!warehouse.location?.address?.street) nullRequiredFields.push("Street");
                if (!warehouse.location?.address?.number) nullRequiredFields.push("Number");
                if (!warehouse.location?.address?.complement) nullRequiredFields.push("Complement");
                if (!warehouse.location?.address?.city) nullRequiredFields.push("City");
                if (!warehouse.location?.address?.state) nullRequiredFields.push("State");
                if (!warehouse.location?.address?.country) nullRequiredFields.push("Country");
                // if (!warehouse.location?.address?.zipCode) nullRequiredFields.push("Zip Code");

                if (nullRequiredFields.length > 0) {
                    setInvalidFields(new Set(nullRequiredFields));
                    const warnMessage: string =
                        nullRequiredFields.length === 1
                            ? getUiInterface("fieldRequiredSingle", {
                                field: nullRequiredFields[0],
                            })
                            : getUiInterface("fieldRequiredMultiple", {
                                fields: `${nullRequiredFields.slice(0, -1).join(", ")}${nullRequiredFields.length > 1
                                    ? ` ${getWordConnector("and")} `
                                    : ""
                                    }${nullRequiredFields.slice(-1)}`,
                            });

                    setAlert({ message: warnMessage, variant: "danger" });
                    return;
                } else {
                    setInvalidFields(new Set());
                }

                const sucesso = await putWarehouse(warehouse.id, warehouse);

                if (sucesso) {
                    const successMessage = getSuccessMessage("editWarehouse", { id: warehouse.id, });
                    navigate(`/Warehouse`, {
                        state: {
                            message: successMessage,
                            variant: "success",
                        },
                    });
                }
                else {
                    const errorMessage = getErrorMessage("editWarehouseError", { id: warehouse.id, });
                    setAlert({ message: errorMessage, variant: "danger" });
                }
            } catch (error) {
                setAlert({ message: "Erro ao atualizar os dados.", variant: "danger" });
            }
        } else {
            setIsEditing(true);
        }
    };


    const handleDelete = async () => {
        if (!id) return;

        try {
            const sucesso = await excluiWarehouse(Number(id));

            if (sucesso) {
                const successMessage = getSuccessMessage("deleteWarehouse", { id: id });
                navigate(`/warehouse`, {
                    state: {
                        message: successMessage,
                        variant: "success",
                    },
                });
            }
            else {
                const errorMessage = getErrorMessage("deleteWarehouseError", { id: id, });
                setAlert({ message: errorMessage, variant: "danger" });
            }
        } catch (error) {
            setAlert({ message: "Erro ao excluir o registro.", variant: "danger" });
        }
    };


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (warehouse) {
            const { name, value } = e.target;
    
            setWarehouse((prevWarehouse) => {
                const keys = name.split(".");
                const updatedWarehouse = { ...prevWarehouse };
                let current: any = updatedWarehouse;
    
                for (let i = 0; i < keys.length - 1; i++) {
                    const key = keys[i];
                    current[key] = { ...current[key] };
                    current = current[key];
                }
    
                current[keys[keys.length - 1]] = value;
                
                return updatedWarehouse;
            });
        }
    };

    const hadleChangeColor = (newColor: string) => {
        setWarehouse((prevState) => ({
            ...prevState,
            color: newColor.replaceAll("#", ""),
        }));
    };

    const hadleChangeColorless = (newColor: string) => {
        setWarehouse((prevState) => ({
            ...prevState,
            colorless: newColor.replaceAll("#", ""),
        }));
    };

    const hadleChangeFontColor = (newColor: string) => {
        setWarehouse((prevState) => ({
            ...prevState,
            fontColor: newColor.replaceAll("#", ""),
        }));
    };

    const handleChangeSz = (sz: any) => {
        setWarehouse((prevState) => ({
            ...prevState,
            sz: sz.target.checked,
        }));
    };

    if (loading) {
        return <div className="text-center mt-4">Carregando...</div>;
    }

    if (error || !warehouse) {
        return <div className="text-center mt-4 text-danger">Erro ao carregar os dados.</div>;
    }

    return (
        <Container fluid>
            <NavBar items={navItems} />
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}
            <div className="mb-3"></div>
            <h2>{isEditing ? getPageName("editWarehouse") : getPageName("viewWarehouse")}</h2>

            {successMessage && (
                <div style={{ fontSize: "14px", color: "green", marginTop: "5px" }}>
                    {successMessage}
                </div>
            )}

            <Form className="mt-3">
                <Row>
                    <Col md={4} className="mb-3">
                        <Form.Group>
                            <Form.Label>{t('warehouse.fields.id')}</Form.Label>
                            <Form.Control size="sm" readOnly value={warehouse.id} style={{ width: "100%", backgroundColor: "#f0f0f0" }} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={4} className="mb-3">
                        <Form.Group controlId="name">
                            <Form.Label>{t('warehouse.fields.name')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={warehouse.name}
                                isInvalid={invalidFields.has(t('warehouse.fields.name'))}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                        <Form.Group controlId="identification">
                            <Form.Label>{t('warehouse.fields.identification')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="identification"
                                value={warehouse.identification}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                        <Form.Group controlId="cnpj">
                            <Form.Label>{t('warehouse.fields.cnpj')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="cnpj"
                                value={warehouse.cnpj}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                
                <Row>
                    <Col md={4} className="mb-3">
                        <Form.Group controlId="color">
                            <Form.Label>{t('warehouse.fields.color')}</Form.Label>
                            <div style={{display: "flex", alignItems: "center"}}>
                                <Form.Control
                                    type="text"
                                    name="color"
                                    value={warehouse.color}
                                    onChange={handleChange}
                                    readOnly={!isEditing}
                                    style={{ 
                                        width: "calc(100% - 64px)", 
                                        display: "inline-block", 
                                        marginRight: "8px",
                                        backgroundColor: isEditing ? "white" : "#f0f0f0"
                                    }}
                                />
                                <ColorPicker
                                    color={`#${warehouse.color}`}
                                    onChange={(newColor) =>
                                        hadleChangeColor(newColor)
                                    }
                                    blocked={!isEditing}
                                />
                            </div>
                        </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                        <Form.Group controlId="colorless">
                            <Form.Label>{t('warehouse.fields.colorless')}</Form.Label>
                            <div style={{display: "flex", alignItems: "center"}}>
                                <Form.Control
                                    type="text"
                                    name="colorless"
                                    value={warehouse.colorless}
                                    readOnly={!isEditing}
                                    onChange={handleChange}
                                    style={{ 
                                        width: "calc(100% - 64px)", 
                                        display: "inline-block", 
                                        marginRight: "8px",
                                        backgroundColor: isEditing ? "white" : "#f0f0f0"
                                    }}
                                />
                                <ColorPicker
                                    color={`#${warehouse.colorless}`}
                                    onChange={(newColor) =>
                                        hadleChangeColorless(newColor)
                                    }
                                    blocked={!isEditing}
                                />
                            </div>
                        </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                        <Form.Group controlId="fontColor">
                            <Form.Label>{t('warehouse.fields.fontColor')}</Form.Label>
                            <div style={{display: "flex", alignItems: "center"}}>
                                <Form.Control
                                    type="text"
                                    name="fontColor"
                                    value={warehouse.fontColor}
                                    readOnly={!isEditing}
                                    onChange={handleChange}
                                    style={{ 
                                        width: "calc(100% - 64px)", 
                                        display: "inline-block", 
                                        marginRight: "8px",
                                        backgroundColor: isEditing ? "white" : "#f0f0f0"
                                    }}
                                />
                                <ColorPicker
                                    color={`#${warehouse.fontColor}`}
                                    onChange={(newColor) =>
                                        hadleChangeFontColor(newColor)
                                    }
                                    blocked={!isEditing}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                
                <Row>
                    <Col md={4} className="mb-3">
                        <div style={{display: "flex", alignItems: "center"}}>
                            <Form.Group controlId="sz">
                                <Form.Check
                                    type="checkbox"
                                    label={t('warehouse.fields.sz')}
                                    name="sz"
                                    checked={warehouse.sz}
                                    disabled={!isEditing}
                                    onChange={handleChangeSz}
                                />
                            </Form.Group>
                        </div>
                    </Col>
                </Row>
                
                <Row>
                    <h5>{t('warehouse.labels.location')}</h5>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lat">
                            <Form.Label>{t('warehouse.fields.lat')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.latitude"
                                value={warehouse.location?.latitude}
                                isInvalid={invalidFields.has(t('warehouse.fields.lat'))}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lng">
                            <Form.Label>{t('warehouse.fields.lng')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.longitude"
                                value={warehouse.location?.longitude}
                                isInvalid={invalidFields.has(t('warehouse.fields.lng'))}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <h6>{t('warehouse.labels.address')}</h6>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="street">
                            <Form.Label>{t('warehouse.fields.street')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.street"
                                value={warehouse.location?.address?.street}
                                isInvalid={invalidFields.has(t('warehouse.fields.street'))}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="mb-3">
                        <Form.Group controlId="number">
                            <Form.Label>{t('warehouse.fields.number')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.number"
                                value={warehouse.location?.address?.number}
                                isInvalid={invalidFields.has(t('warehouse.fields.number'))}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="complement">
                            <Form.Label>{t('warehouse.fields.complement')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.complement"
                                value={warehouse.location?.address?.complement}
                                isInvalid={invalidFields.has(t('warehouse.fields.complement'))}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="city">
                            <Form.Label>{t('warehouse.fields.city')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.city"
                                value={warehouse.location?.address?.city}
                                isInvalid={invalidFields.has(t('warehouse.fields.city'))}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="state">
                            <Form.Label>{t('warehouse.fields.state')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.state"
                                value={warehouse.location?.address?.state}
                                isInvalid={invalidFields.has(t('warehouse.fields.state'))}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                
                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="country">
                            <Form.Label>{t('warehouse.fields.country')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.country"
                                value={warehouse.location?.address?.country}
                                isInvalid={invalidFields.has(t('warehouse.fields.country'))}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    {/* <Col md={6} className="mb-3">
                        <Form.Group controlId="zipCode">
                            <Form.Label>Zip Code</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.zipCode"
                                value={warehouse.location?.address?.zipCode}
                                isInvalid={invalidFields.has("Zip Code")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col> */}
                </Row>
            </Form>

            <div className="mt-3 mb-3 d-flex gap-2">
                <Button variant="primary" onClick={handleEdit}>
                    {isEditing ? getUiInterface("save") : getUiInterface("edit")}
                </Button>
                <Button variant="danger" onClick={() => setShowDeleteModal(true)}>{getUiInterface("delete")}</Button>
            </div>

            <ConfirmDeleteModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
            />
        </Container>
    );
};

export default ViewWarehouseById;
